// ===== Preload tab UI =====
// Edit the playable's initial install/preload screen — the game name + app icon
// shown while the ad loads.
//
// Two source modes:
//   • 업로드 (main)  — drop/pick an external HTML file; edit → download the patched file.
//   • 기존 소재 (sub) — pick 타이틀 → 대분류 → 소분류(file) from the library; edit →
//                       save straight back into that file's base build (all canvases inherit it).
// The name/icon are parsed from the source HTML, live-previewed in a phone frame,
// and patched in place (no multi-MB reload per keystroke).

import { escapeHtml, toast } from './util.js';
import { buildDropdown } from './ui/dropdown.js';
import { titles } from './session.js';
import { parsePreload, patchPreload } from './editor.js';
import { fetchFileSource, replaceFileBase } from './api.js';

const root = document.getElementById('preloadRoot');

const TITLE_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>`;
const CREATIVE_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 3v18"/></svg>`;
const FILE_ICON = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>`;

// DOM ids the Luna preload template emits (patched live in the preview iframe).
const NAME_EL_ID = 'parameter/preloader/name';
const ICON_EL_ID = 'asset/preloader/icon';

let sourceMode = 'upload';        // 'upload' | 'library'
let pl = { titleId: null, creativeId: null, fileId: null };  // library picker selection

// Loaded editing state (shared by both modes).
let baseSource = '';              // source HTML currently being edited
let baseBytes = 0;                // byte size of baseSource (cached for the size badge)
let defaults = {};                // { name?, icon? } parsed from baseSource
let edits = {};                   // current values (seeded from defaults, then user-edited)
let hasPreload = false;           // whether the source exposes preload elements
let loadedFrom = null;            // { type:'upload', name } | { type:'library', fileId, fileName }
let loading = false;

let blobUrl = null;
let saveInFlight = false;
let previewLandscape = false;     // preview-only: rotate the device frame to check landscape/other aspects
let animateIn = false;            // play the settings/preview entrance animation on the next editor render
let manipSel = null;              // currently selected element in the preview: 'name' | 'icon' | null

function curTitle() { return titles.find(t => t.id === pl.titleId) || null; }
function curCreative() { const t = curTitle(); return t ? (t.creatives.find(c => c.id === pl.creativeId) || null) : null; }
function curFile() { const c = curCreative(); return c ? ((c.files || []).find(f => f.id === pl.fileId) || null) : null; }

// Icon asset kinds. The template hook is an <img>; a 'video' pick swaps it for a
// <video> on live-preview + export (see setIconMedia / patchLiveIcon).
const ICON_ACCEPT = {
  image: 'image/png,image/jpeg,image/webp,image/svg+xml',
  gif: 'image/gif,.gif',
  video: 'video/mp4,video/webm,.mp4,.webm',
};
const ICON_PICK_LABEL = { image: '이미지 선택', gif: 'GIF 선택', video: '영상 선택' };

// Assets are embedded as base64 data URLs, so a big file bloats the playable well
// past ad-network size caps — hold icon/GIF/video picks to 5MB.
const MAX_ICON_MB = 5;
const MAX_ICON_BYTES = MAX_ICON_MB * 1024 * 1024;

// Icon hook animations — keep viewers watching the preload. Applied via a <style>
// on the icon id (live + export). The transform-based ones compose with the
// layout's individual `translate` centering (see layoutRule), so a repositioned
// icon still animates in place.
const ICON_FX = [
  { key: 'none',   label: '없음' },
  { key: 'fill',   label: '차오름 (아래→위)' },
  { key: 'bounce', label: '통통 튀기' },
  { key: 'pulse',  label: '두근두근' },
  { key: 'float',  label: '둥실둥실' },
  { key: 'shake',  label: '흔들흔들' },
  { key: 'glow',   label: '반짝임' },
  { key: 'spin',   label: '회전' },
  { key: 'zoom',   label: '줌 인·아웃' },
];
const ICON_FX_ANIM = {
  fill:   { kf: '@keyframes __pawFxFill{from{clip-path:inset(100% 0 0 0)}to{clip-path:inset(0 0 0 0)}}', anim: '__pawFxFill 2.4s ease-in-out infinite alternate' },
  bounce: { kf: '@keyframes __pawFxBounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-18%)}}', anim: '__pawFxBounce .9s cubic-bezier(.28,.84,.42,1) infinite' },
  pulse:  { kf: '@keyframes __pawFxPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.13)}}', anim: '__pawFxPulse 1.1s ease-in-out infinite' },
  float:  { kf: '@keyframes __pawFxFloat{0%,100%{transform:translateY(0)}50%{transform:translateY(-9%)}}', anim: '__pawFxFloat 2.6s ease-in-out infinite' },
  shake:  { kf: '@keyframes __pawFxShake{0%,100%{transform:rotate(0)}20%{transform:rotate(-7deg)}40%{transform:rotate(7deg)}60%{transform:rotate(-5deg)}80%{transform:rotate(5deg)}}', anim: '__pawFxShake .8s ease-in-out infinite' },
  glow:   { kf: '@keyframes __pawFxGlow{0%,100%{filter:drop-shadow(0 0 0 rgba(255,255,255,0))}50%{filter:drop-shadow(0 0 14px rgba(255,255,255,.95))}}', anim: '__pawFxGlow 1.4s ease-in-out infinite' },
  spin:   { kf: '@keyframes __pawFxSpin{from{transform:rotate(0)}to{transform:rotate(360deg)}}', anim: '__pawFxSpin 2.6s linear infinite' },
  zoom:   { kf: '@keyframes __pawFxZoom{0%,100%{transform:scale(.88)}50%{transform:scale(1.1)}}', anim: '__pawFxZoom 1.8s ease-in-out infinite' },
};
function iconFxCss(fx) {
  const e = ICON_FX_ANIM[fx];
  if (!e) return '';
  return `${e.kf}[id="asset/preloader/icon"]{animation:${e.anim}!important;transform-origin:center center!important}`;
}

// Fullscreen video: fill the whole screen (cover). Overrides position/size/radius,
// and while active the icon can't be dragged/resized.
// Hide every native video control (progress/play bar, the iOS tap-to-play overlay)
// so the clip reads as pure motion — it autoplays muted, no chrome. Applied to any
// icon video (live + export). Doesn't touch pointer-events, so preview dragging works.
function iconVideoCss() {
  const s = '[id="asset/preloader/icon"]';
  return `${s}::-webkit-media-controls,${s}::-webkit-media-controls-enclosure,`
       + `${s}::-webkit-media-controls-panel,${s}::-webkit-media-controls-start-playback-button,`
       + `${s}::-webkit-media-controls-overlay-play-button`
       + `{display:none!important;-webkit-appearance:none!important;opacity:0!important}`;
}
function iconFullscreenActive() { return edits.iconType === 'video' && !!edits.iconFullscreen; }
function iconFullscreenCss() {
  return `[id="asset/preloader/icon"]{position:fixed!important;inset:0!important;`
       + `width:100vw!important;height:100vh!important;max-width:none!important;margin:0!important;`
       + `translate:none!important;transform:none!important;border-radius:0!important;`
       + `object-fit:cover!important;z-index:2147483000!important}`;
}

// ===== Icon size optimization =====
// The preload icon displays small, so a big upload is wasted bytes in the export.
// Downscale a raster image (png/jpg/webp) to a display-sized WebP — SAFE (it's our
// own asset, not the base playable, so no execution/ad-network-rejection risk).
// SVG (vector, tiny), GIF and video are left untouched (re-encoding would drop
// their animation). Never upscales; only used when it actually shrinks.
const ICON_MAX_PX = 512;
const fmtBytes = (n) => n < 1048576 ? `${Math.round(n / 1024)}KB` : `${(n / 1048576).toFixed(2)}MB`;

function readAsDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result));
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}
async function optimizeIconImage(file, rawDataUrl) {
  if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) return null;   // raster only
  let img;
  try {
    img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = rawDataUrl; });
  } catch { return null; }
  const scale = Math.min(1, ICON_MAX_PX / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * scale));
  const h = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d'); if (!ctx) return null;
  ctx.drawImage(img, 0, 0, w, h);
  let out;
  try { out = canvas.toDataURL('image/webp', 0.92); } catch { return null; }
  if (!/^data:image\/webp/i.test(out || '')) return null;          // browser lacks WebP encode → skip
  return out.length < rawDataUrl.length ? out : null;              // only if it actually shrinks
}

// Ad-network size cap. The base playable alone is often ~5MB, so this is a
// non-blocking WARNING on the exported file (a hard block would stop most
// legitimate downloads) — surfaces oversize output without breaking the tool.
const OUTPUT_WARN_MB = 5;
const OUTPUT_WARN_BYTES = OUTPUT_WARN_MB * 1024 * 1024;

function guessIconType(icon) {
  if (/^data:video\//i.test(icon || '')) return 'video';
  if (/^data:image\/gif/i.test(icon || '')) return 'gif';
  return 'image';
}

// Preview thumbnail markup for the picked icon (a <video> for clips, else <img>).
function iconThumbHtml() {
  const src = escapeHtml(edits.icon || '');
  const style = `border-radius:${edits.iconRadius ?? 25}%`;
  if (edits.iconType === 'video')
    return `<video class="ed-image-preview" style="${style}" src="${src}" autoplay loop muted playsinline></video>`;
  return `<img class="ed-image-preview" style="${style}" alt="app icon" src="${src}">`;
}

function dirty() {
  return (edits.name !== (defaults.name ?? ''))
      || (edits.icon !== (defaults.icon ?? ''))
      || (edits.nameVisible !== defaults.nameVisible)
      || (edits.bgColor !== defaults.bgColor)
      || (edits.gaugeOn !== defaults.gaugeOn)
      || (edits.iconType !== defaults.iconType)
      || (edits.iconRadius !== defaults.iconRadius)
      || (edits.iconFx !== defaults.iconFx)
      || (edits.iconFullscreen !== defaults.iconFullscreen)
      || (edits.barColor !== defaults.barColor)
      || JSON.stringify(edits.layout) !== JSON.stringify(defaults.layout);
}

// ===== Top-level render =====
function render() {
  if (!root) return;
  root.innerHTML = `
    <div class="cv-head">
      <h2>프리로드 화면</h2>
      <p>플레이어블 광고가 로딩되는 동안 표시되는 초기 프리로드 화면(게임 이름 · 앱 아이콘)을 편집합니다.</p>
    </div>
    <div class="pl-grid">
      <div class="pl-main">
        <div class="cv-source-tabs" id="plSourceTabs">
          <button type="button" class="cv-stab${sourceMode === 'upload' ? ' active' : ''}" data-mode="upload">HTML 업로드</button>
          <button type="button" class="cv-stab${sourceMode === 'library' ? ' active' : ''}" data-mode="library">기존 소재</button>
        </div>
        <div class="cv-source-body" id="plSourceBody"></div>
        <div id="plEditor"></div>
      </div>
      <div class="pl-preview-col" id="plPreviewHost"></div>
    </div>
  `;
  root.querySelectorAll('#plSourceTabs .cv-stab').forEach(btn => {
    btn.addEventListener('click', () => {
      if (sourceMode === btn.dataset.mode) return;
      sourceMode = btn.dataset.mode;
      resetLoaded();
      render();
    });
  });
  renderSourceBody();
  renderEditor();
}

// ===== Source body =====
function renderSourceBody() {
  const body = root.querySelector('#plSourceBody');
  if (!body) return;
  if (sourceMode === 'upload') renderUpload(body);
  else renderPick(body);
}

function renderUpload(body) {
  body.innerHTML = `
    <div class="cv-drop" id="plDrop">
      <input type="file" id="plFileInput" accept=".html,.htm" hidden>
      <div class="cv-drop-inner">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
        <div class="cv-drop-text">프리로드가 포함된 HTML을 끌어다 놓거나 <button class="cv-link" id="plBrowse">찾아보기</button></div>
        <div class="cv-drop-sub">.html · 편집 후 다운로드</div>
      </div>
    </div>`;
  const drop = body.querySelector('#plDrop');
  const fileInput = body.querySelector('#plFileInput');
  body.querySelector('#plBrowse').addEventListener('click', (e) => { e.preventDefault(); fileInput.click(); });
  drop.addEventListener('click', (e) => { if (!e.target.closest('button')) fileInput.click(); });
  fileInput.addEventListener('change', (e) => { const f = e.target.files[0]; if (f) loadUploadFile(f); e.target.value = ''; });
  ['dragenter', 'dragover'].forEach(ev =>
    drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('drag'); }));
  ['dragleave', 'drop'].forEach(ev =>
    drop.addEventListener(ev, (e) => {
      e.preventDefault();
      if (ev === 'dragleave' && drop.contains(e.relatedTarget)) return;
      drop.classList.remove('drag');
    }));
  drop.addEventListener('drop', (e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) loadUploadFile(f); });
}

function pickRow(label, items, selectedId, placeholder, icon, onSelect) {
  const sec = document.createElement('div');
  sec.className = 'cv-pick-row';
  sec.innerHTML = `<div class="cv-pick-label">${label}</div>`;
  sec.appendChild(buildDropdown({
    items, selectedId, placeholder, searchPlaceholder: placeholder, triggerIcon: icon, onSelect,
  }));
  return sec;
}

function renderPick(body) {
  body.innerHTML = `<div class="cv-creative-pick" id="plPick"></div>`;
  const pick = body.querySelector('#plPick');

  pick.appendChild(pickRow('타이틀',
    titles.map(t => ({ id: t.id, name: t.name, meta: `${t.creatives.length} 대분류`, icon: TITLE_ICON })),
    pl.titleId, '타이틀 선택', TITLE_ICON,
    (item) => { pl = { titleId: item.id, creativeId: null, fileId: null }; resetLoaded(); renderSourceBody(); renderEditor(); }));

  const t = curTitle();
  if (t) {
    pick.appendChild(pickRow('대분류',
      t.creatives.map(c => ({ id: c.id, name: c.name, meta: `${(c.files || []).length} 소분류`, icon: CREATIVE_ICON })),
      pl.creativeId, '대분류 선택', CREATIVE_ICON,
      (item) => { pl.creativeId = item.id; pl.fileId = null; resetLoaded(); renderSourceBody(); renderEditor(); }));
  }

  const c = curCreative();
  if (c) {
    pick.appendChild(pickRow('소분류(파일)',
      (c.files || []).map(f => ({ id: f.id, name: f.name, meta: `${(f.canvases || []).length}개 캔버스`, icon: FILE_ICON })),
      pl.fileId, '파일 선택', FILE_ICON,
      (item) => { pl.fileId = item.id; renderSourceBody(); loadLibraryFile(item.id); }));
  }

  if ((titles || []).length === 0) {
    pick.appendChild(Object.assign(document.createElement('div'), {
      className: 'cv-note',
      textContent: '저장된 소재가 없습니다. 상단의 "HTML 업로드"로 외부 파일을 편집하세요.',
    }));
  }
}

function resetLoaded() {
  baseSource = '';
  baseBytes = 0;
  defaults = {};
  edits = {};
  hasPreload = false;
  loadedFrom = null;
  loading = false;
  manipSel = null;
  revokeBlob();
}

function revokeBlob() {
  if (blobUrl) { URL.revokeObjectURL(blobUrl); blobUrl = null; }
}

// ===== Load sources =====
async function loadUploadFile(file) {
  if (!/\.html?$/i.test(file.name)) { toast('HTML 파일만 편집할 수 있습니다'); return; }
  loading = true;
  renderEditor();
  try {
    const text = await file.text();
    setLoaded(text, { type: 'upload', name: file.name });
  } catch (e) {
    console.warn('[preload] file read failed:', e.message);
    toast('파일을 읽지 못했습니다');
    resetLoaded();
  } finally {
    loading = false;
    renderEditor();
  }
}

async function loadLibraryFile(fileId) {
  const f = curFile();
  if (!f) { resetLoaded(); renderEditor(); return; }
  loading = true;
  renderEditor();
  try {
    const text = await fetchFileSource(fileId);
    if (pl.fileId !== fileId) return;   // selection changed mid-flight
    setLoaded(text, { type: 'library', fileId, fileName: f.name });
  } catch (e) {
    console.warn('[preload] source load failed:', e.message);
    toast('원본 로드 실패');
    resetLoaded();
  } finally {
    loading = false;
    renderEditor();
  }
}

function setLoaded(text, from) {
  baseSource = text;
  baseBytes = new Blob([text]).size;
  const parsed = parsePreload(text) || {};
  // Baseline (what the source currently is). name/icon come from the DOM; the
  // extra toggles (visibility / bg) are our own overrides — detect any we baked
  // in on a previous save so re-editing diffs correctly.
  defaults = {
    name: parsed.name,
    icon: parsed.icon,
    nameVisible: !/id="__pawHideName"/i.test(text),
    iconType: guessIconType(parsed.icon),
    iconRadius: parseIconRadius(text),
    iconFx: parseIconFx(text),
    iconFullscreen: parseIconFullscreen(text),
    // Background + progress-bar color are always applied; defaults black / white.
    bgColor: parseBakedBg(text) || '#000000',
  };
  defaults.gaugeSelector = detectGauge(text) || '';
  defaults.gaugeOn = !/id="__pawHideGauge"/i.test(text);
  defaults.barColor = parseBakedBarColor(text) || '#ffffff';
  defaults.layout = parseBakedLayout(text, defaults.gaugeSelector);   // { name?, icon?, gauge? }
  hasPreload = parsed.name !== undefined || parsed.icon !== undefined;
  edits = {
    name: defaults.name ?? '',
    icon: defaults.icon ?? '',
    nameVisible: defaults.nameVisible,
    iconType: defaults.iconType,
    iconRadius: defaults.iconRadius,
    iconFx: defaults.iconFx,
    iconFullscreen: defaults.iconFullscreen,
    bgColor: defaults.bgColor,
    gaugeSelector: defaults.gaugeSelector,
    gaugeOn: defaults.gaugeOn,
    barColor: defaults.barColor,
    layout: cloneLayout(defaults.layout),
  };
  manipSel = null;
  loadedFrom = from;
  animateIn = true;   // freshly loaded → play the entrance animation on render
}

// Layout is stored PER ORIENTATION — a portrait layout and a landscape layout can
// differ (elements sit differently on a tall vs. wide screen), so the two can't
// share one set of positions without one of them breaking on rotation. Each holds
// { name, icon, gauge } overrides; export bakes them under @media (orientation).
function orientKey() { return previewLandscape ? 'landscape' : 'portrait'; }
function curLayout() { return edits.layout ? edits.layout[orientKey()] : null; }
function emptyLayout() { return { name: null, icon: null, gauge: null }; }
function emptyLayouts() { return { portrait: emptyLayout(), landscape: emptyLayout() }; }

function cloneLayout(l) {
  const one = (o) => ({
    name: o && o.name ? { ...o.name } : null,
    icon: o && o.icon ? { ...o.icon } : null,
    gauge: o && o.gauge ? { ...o.gauge } : null,
  });
  return { portrait: one(l && l.portrait), landscape: one(l && l.landscape) };
}

// Best-effort detection of the preload gauge (loading bar) element. Luna/Playworks
// templates name it structurally (e.g. .preloader__progress, .loading-bar) rather
// than via a dynamic id, so we match common gauge-ish tokens and derive a stable
// selector. Returns a CSS selector string, or '' if nothing convincing is found.
function detectGauge(text) {
  let doc;
  try { doc = new DOMParser().parseFromString(text, 'text/html'); } catch { return ''; }
  return pickGauge(doc.body ? [...doc.body.querySelectorAll('*')] : []);
}

// A gauge is a progress/gauge, or a "meter"/"bar" as a whole segment — the segment
// guard (^ or -/_ … then meter|bar … then -/_ or $) matches preloader__bar /
// outer__bar / loading-bar but NOT toolbar/sidebar, and NOT "meter" hiding inside
// "para·meter" — which every Luna `parameter/...` id contains, so an unguarded
// `meter` false-matches the preload name/color params (and the color overlay,
// wrapping the real bar, would then win on containment).
// Among candidates we prefer the OUTERMOST (a track containing the fill) and
// outer/track/container-style names, so the whole gauge moves, not just its fill.
const GAUGE_TOK = /progress|gauge|(^|[-_])(meter|bar)([-_]|$)/i;
const GAUGE_OUTER = /outer|track|container|holder|wrap|bg|background/i;
function isGaugeish(el) {
  if (el.id === 'parameter/preloader/name' || el.id === 'asset/preloader/icon') return false;
  return GAUGE_TOK.test(`${el.id || ''} ${classString(el)}`);
}
function pickGauge(els) {
  const matches = els.filter(isGaugeish);
  if (!matches.length) return '';
  let best = matches[0], bestScore = -Infinity;
  for (const el of matches) {
    let score = matches.reduce((n, o) => n + (o !== el && el.contains(o) ? 1 : 0), 0);
    if (GAUGE_OUTER.test(classString(el))) score += 0.5;
    if (score > bestScore) { bestScore = score; best = el; }
  }
  return selectorFor(best, GAUGE_TOK);
}
function classString(el) {
  const c = el.className;
  return typeof c === 'string' ? c : (c && c.baseVal) || '';
}
function selectorFor(el, rx) {
  if (el.id) return `[id="${el.id}"]`;
  const tokens = classString(el).trim().split(/\s+/).filter(Boolean);
  const tok = tokens.find(t => rx.test(t)) || tokens[0];
  return tok ? `.${tok}` : '';
}

// Recover per-orientation drag layout (from our __pawLayout style) so re-opening a
// saved file restores the override objects. Each orientation lives in its own
// @media (orientation:…) block.
function parseBakedLayout(text, gaugeSelector) {
  const block = text.match(/id="__pawLayout">([\s\S]*?)<\/style>/i);
  const out = emptyLayouts();
  if (!block) return out;
  const body = block[1];
  const mediaBody = (orient) => {
    const m = body.match(new RegExp(`@media \\(orientation:${orient}\\)\\{([\\s\\S]*?)\\}(?=@media|$)`, 'i'));
    return m ? m[1] : '';
  };
  const parseRules = (bstr) => {
    const o = emptyLayout();
    if (!bstr) return o;
    const ruleBody = (selector) => {
      if (!selector) return null;
      const esc = selector.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const rule = bstr.match(new RegExp(`${esc}\\{([^}]*)\\}`));
      return rule ? rule[1] : null;
    };
    const pos = (s) => {
      const left = s.match(/left:\s*([\d.]+)%/), top = s.match(/top:\s*([\d.]+)%/);
      return (left && top) ? { x: +left[1], y: +top[1] } : null;
    };
    const nameBody = ruleBody('[id="parameter/preloader/name"]');
    if (nameBody) {
      const p = pos(nameBody), fs = nameBody.match(/font-size:\s*([\d.]+)vmax/);
      if (p && fs) o.name = { ...p, size: +fs[1] };
    }
    const box = (bodyStr) => {
      if (!bodyStr) return null;
      const p = pos(bodyStr);
      const w = bodyStr.match(/width:\s*([\d.]+)vmax/), h = bodyStr.match(/height:\s*([\d.]+)vmax/);
      return (p && w && h) ? { ...p, w: +w[1], h: +h[1] } : null;
    };
    o.icon = box(ruleBody('[id="asset/preloader/icon"]'));
    o.gauge = box(ruleBody(gaugeSelector));
    return o;
  };
  out.portrait = parseRules(mediaBody('portrait'));
  out.landscape = parseRules(mediaBody('landscape'));
  return out;
}

// Recover a previously baked-in background color (from our __pawBg style) so the
// picker re-opens on the saved value instead of the placeholder default.
function parseBakedBg(text) {
  const m = text.match(/id="__pawBg">[\s\S]*?background:\s*(#[0-9a-fA-F]{3,8})/i);
  return m ? m[1] : null;
}

// Recover a previously baked-in progress-bar color (from our __pawBarColor style).
function parseBakedBarColor(text) {
  const m = text.match(/id="__pawBarColor">[\s\S]*?background:\s*(#[0-9a-fA-F]{3,8})/i);
  return m ? m[1] : null;
}

// Icon corner radius (%) baseline: our baked __pawIconRadius override if present,
// else the template's own border-radius for the icon's class, else 25%.
// The icon hook animation baked in a previous save (from our __pawIconFx style).
function parseIconFx(text) {
  const m = text.match(/id="__pawIconFx">[\s\S]*?animation:__pawFx(\w+)/i);
  const key = m ? m[1].toLowerCase() : 'none';
  return ICON_FX_ANIM[key] ? key : 'none';
}
function parseIconFullscreen(text) { return /id="__pawIconFull"/i.test(text); }

function parseIconRadius(text) {
  const baked = text.match(/id="__pawIconRadius">[\s\S]*?border-radius:\s*([\d.]+)%/i);
  if (baked) return +baked[1];
  const tag = text.match(/<(?:img|video)[^>]*id="asset\/preloader\/icon"[^>]*>/i);
  const clsM = tag && tag[0].match(/\sclass="([^"]*)"/i);
  if (clsM) {
    for (const c of clsM[1].trim().split(/\s+/)) {
      const esc = c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const rule = text.match(new RegExp(`\\.${esc}\\s*\\{[^}]*?border-radius:\\s*([\\d.]+)%`, 'i'));
      if (rule) return +rule[1];
    }
  }
  return 25;
}

// ===== Editor =====
function renderEditor() {
  const host = root && root.querySelector('#plEditor');
  const previewHost = root && root.querySelector('#plPreviewHost');
  if (!host) return;
  if (previewHost) previewHost.innerHTML = '';   // preview only shows once a preload is loaded
  // `pl-loaded` on the root drives the layout: centered picker when empty →
  // two-column editor once a source with a preload is loaded.
  const setLoaded = (on) => root.classList.toggle('pl-loaded', on);

  if (loading) { host.innerHTML = `<div class="cv-note">원본을 불러오는 중…</div>`; setLoaded(false); return; }
  if (!loadedFrom) { host.innerHTML = ''; setLoaded(false); return; }
  if (!hasPreload) {
    host.innerHTML = `<div class="cv-note">이 HTML에는 프리로드 화면 요소(<code>${escapeHtml(NAME_EL_ID)}</code> · <code>${escapeHtml(ICON_EL_ID)}</code>)가 없습니다. Luna/Playworks 프리로드 템플릿이 포함된 빌드에서만 편집할 수 있습니다.</div>`;
    setLoaded(false);
    return;
  }

  setLoaded(true);
  const anim = animateIn; animateIn = false;   // one-shot: only on the fresh-load transition
  const isUpload = loadedFrom.type === 'upload';
  const srcLabel = isUpload ? loadedFrom.name : loadedFrom.fileName;
  const actionLabel = isUpload ? '다운로드' : '저장';

  host.innerHTML = `
    <div class="pl-srcbar">편집 대상 · <b>${escapeHtml(srcLabel)}</b><button type="button" class="pl-srcbar-x" id="plCancel" title="편집 취소">×</button></div>
    <div class="pl-form${anim ? ' pl-anim-up' : ''}">
        ${defaults.name !== undefined ? `
        <div class="editor-field">
          <div class="pl-field-head">
            <label class="ed-label">앱 이름</label>
            <label class="ed-toggle" title="프리로드 화면에 앱 이름 표시">
              <input type="checkbox" id="plNameShow" ${edits.nameVisible ? 'checked' : ''}>
              <span class="ed-toggle-track"><span class="ed-toggle-thumb"></span></span>
            </label>
          </div>
          <input type="text" class="ed-input" id="plName" value="${escapeHtml(edits.name || '')}" placeholder="프리로드 화면에 표시될 이름"${edits.nameVisible ? '' : ' disabled'}>
          <div class="ed-key">${escapeHtml(NAME_EL_ID)}</div>
        </div>
        <div class="pl-divider"></div>` : ''}
        ${defaults.icon !== undefined ? `
        <div class="editor-field">
          <div class="pl-field-head">
            <label class="ed-label">앱 아이콘</label>
            <div class="ed-segmented pl-asset-seg" id="plIconType">
              <button type="button" class="ed-seg${edits.iconType === 'image' ? ' active' : ''}" data-type="image">이미지</button>
              <button type="button" class="ed-seg${edits.iconType === 'gif' ? ' active' : ''}" data-type="gif">GIF</button>
              <button type="button" class="ed-seg${edits.iconType === 'video' ? ' active' : ''}" data-type="video">영상</button>
            </div>
          </div>
          <div class="ed-image">
            <div class="ed-image-thumb" id="plIconThumb">${iconThumbHtml()}</div>
            <div class="ed-image-actions">
              <button type="button" class="ed-image-pick" id="plIconPick">${ICON_PICK_LABEL[edits.iconType]}</button>
              <button type="button" class="ed-image-reset" id="plIconReset"${(edits.icon === (defaults.icon ?? '') && edits.iconType === defaults.iconType) ? ' hidden' : ''}>기본값</button>
            </div>
            <input type="file" accept="${ICON_ACCEPT[edits.iconType]}" id="plIconFile" hidden>
          </div>
          <div class="pl-radius-row" id="plRadiusRow" style="${edits.iconType === 'image' ? '' : 'display:none'}">
            <span class="pl-sub-label">모서리 라운드</span>
            <input type="range" class="pl-radius" id="plIconRadius" min="0" max="50" step="1" value="${edits.iconRadius}">
            <span class="pl-radius-val" id="plIconRadiusVal">${edits.iconRadius}%</span>
          </div>
          <div class="pl-fs-row" id="plFullscreenRow" style="${edits.iconType === 'video' ? '' : 'display:none'}">
            <span class="pl-sub-label">풀스크린</span>
            <label class="ed-toggle" title="영상을 화면 전체로 채움 (풀스크린 시 크기 조절 불가)">
              <input type="checkbox" id="plIconFull" ${edits.iconFullscreen ? 'checked' : ''}>
              <span class="ed-toggle-track"><span class="ed-toggle-thumb"></span></span>
            </label>
          </div>
          <div class="pl-fx-row" id="plFxRow" style="${edits.iconType === 'image' ? '' : 'display:none'}">
            <span class="pl-sub-label">연출</span>
            <select class="ed-input pl-fx-select" id="plIconFx">
              ${ICON_FX.map(f => `<option value="${f.key}"${edits.iconFx === f.key ? ' selected' : ''}>${f.label}</option>`).join('')}
            </select>
          </div>
          <div class="ed-key">${escapeHtml(ICON_EL_ID)} · 최대 ${MAX_ICON_MB}MB</div>
        </div>
        <div class="pl-divider"></div>` : ''}
        <div class="editor-field">
          <div class="pl-field-head">
            <label class="ed-label">프로그래스바</label>
            <label class="ed-toggle" title="프로그래스바(로딩 진행 바) 표시">
              <input type="checkbox" id="plGaugeOn" ${edits.gaugeOn ? 'checked' : ''}>
              <span class="ed-toggle-track"><span class="ed-toggle-thumb"></span></span>
            </label>
          </div>
          <div class="pl-bar-color">
            <span class="pl-sub-label">색상</span>
            <input type="color" class="pl-color" id="plBarColor" value="${escapeHtml(edits.barColor)}">
            <span class="pl-color-hex" id="plBarHex">${escapeHtml((edits.barColor || '').toUpperCase())}</span>
          </div>
          <details class="pl-advanced" id="plGaugeAdv"${edits.gaugeSelector ? '' : ' open'}>
            <summary class="pl-advanced-sum${edits.gaugeSelector ? '' : ' warn'}">${edits.gaugeSelector ? '고급 · 프로그래스바 요소 선택자' : '⚠ 프로그래스바를 자동 감지하지 못함 — 요소 선택자 지정'}</summary>
            <input type="text" class="ed-input pl-gauge-sel" id="plGaugeSel" value="${escapeHtml(edits.gaugeSelector || '')}" placeholder="예: .preloader__bar">
            <div class="ed-key" id="plGaugeHint">${edits.gaugeSelector ? '자동 감지됨 · 미리보기에서 드래그해 위치·크기 조정' : '표시·색상·위치를 적용할 프로그래스바 요소의 CSS 선택자'}</div>
          </details>
        </div>
        <div class="pl-divider"></div>
        <div class="editor-field">
          <div class="pl-field-head">
            <label class="ed-label">배경색</label>
          </div>
          <div class="pl-color-row">
            <input type="color" class="pl-color" id="plBgColor" value="${escapeHtml(edits.bgColor)}">
            <span class="pl-color-hex" id="plBgHex">${escapeHtml((edits.bgColor || '').toUpperCase())}</span>
          </div>
          <div class="ed-key">background</div>
        </div>
        <div class="pl-divider"></div>
        <div class="pl-actions">
          <button class="primary" id="plSave">${actionLabel}</button>
          <button class="cv-ghost" id="plResetBtn" type="button">되돌리기</button>
          <span class="cv-hint" id="plHint"></span>
          <span class="pl-size" id="plSize" title="예상 출력 용량 (4MB 미만 초록 · 5MB 미만 노랑 · 5MB 이상 빨강)"></span>
        </div>
    </div>`;

  if (previewHost) previewHost.innerHTML = `
    <div class="pl-preview${anim ? ' pl-anim-right' : ''}">
      <div class="ed-segmented pl-orient" id="plOrient">
        <button type="button" class="ed-seg${previewLandscape ? '' : ' active'}" data-orient="portrait">세로</button>
        <button type="button" class="ed-seg${previewLandscape ? ' active' : ''}" data-orient="landscape">가로</button>
      </div>
      <div class="pl-phone${previewLandscape ? ' landscape' : ''}">
        <!-- No allow-scripts: the game engine never boots, so only the initial
             preload screen renders (never covered by gameplay). -->
        <iframe id="plFrame" sandbox="allow-same-origin" scrolling="no"></iframe>
      </div>
      <div class="pl-preview-cap" id="plManipHint">요소를 드래그해 위치 이동 · 모서리 핸들로 크기 조정</div>
      <button type="button" class="cv-link pl-layout-reset" id="plLayoutReset">위치·크기 초기화</button>
    </div>`;

  bindEditor(host);
  loadPreview();
  updateSaveState();
}

function bindEditor(host) {
  // Cancel the current edit target → back to the source picker (empty state).
  host.querySelector('#plCancel')?.addEventListener('click', () => {
    if (loadedFrom && loadedFrom.type === 'library') pl.fileId = null;
    resetLoaded();
    render();
  });

  const nameInp = host.querySelector('#plName');
  if (nameInp) {
    nameInp.addEventListener('input', () => {
      edits.name = nameInp.value;
      patchLiveName(nameInp.value);
      updateSaveState();
    });
  }

  // Game-name show/hide toggle
  const nameShow = host.querySelector('#plNameShow');
  nameShow?.addEventListener('change', () => {
    edits.nameVisible = nameShow.checked;
    if (nameInp) nameInp.disabled = !nameShow.checked;
    patchLiveNameVisibility(nameShow.checked);
    updateSaveState();
  });

  // Asset type (이미지 / GIF / 영상) — switches the file-picker filter, label, and
  // (for 영상) swaps the preview element to <video>.
  const iconFile = host.querySelector('#plIconFile');
  const iconReset = host.querySelector('#plIconReset');
  const iconPick = host.querySelector('#plIconPick');

  // Re-sync the icon controls (active segment · accept filter · pick label ·
  // thumbnail) to the current edits.iconType.
  function syncIconControls() {
    host.querySelectorAll('#plIconType .ed-seg').forEach(b =>
      b.classList.toggle('active', b.dataset.type === edits.iconType));
    if (iconFile) iconFile.accept = ICON_ACCEPT[edits.iconType];
    if (iconPick) iconPick.textContent = ICON_PICK_LABEL[edits.iconType];
    const thumb = host.querySelector('#plIconThumb');
    if (thumb) thumb.innerHTML = iconThumbHtml();
    // 모서리 라운드·연출 → 이미지만 · 풀스크린 → 영상만
    const imageOnly = edits.iconType === 'image';
    const radiusRow = host.querySelector('#plRadiusRow');
    if (radiusRow) radiusRow.style.display = imageOnly ? '' : 'none';
    const fxRow = host.querySelector('#plFxRow');
    if (fxRow) fxRow.style.display = imageOnly ? '' : 'none';
    const fsRow = host.querySelector('#plFullscreenRow');
    if (fsRow) fsRow.style.display = edits.iconType === 'video' ? '' : 'none';
  }

  host.querySelectorAll('#plIconType .ed-seg').forEach(btn => {
    btn.addEventListener('click', () => {
      if (edits.iconType === btn.dataset.type) return;
      edits.iconType = btn.dataset.type;
      syncIconControls();          // re-arms the picker; the preview updates on file pick
      updateSaveState();
    });
  });

  iconPick?.addEventListener('click', () => iconFile?.click());
  iconFile?.addEventListener('change', async () => {
    const file = iconFile.files && iconFile.files[0];
    if (!file) return;
    if (file.size > MAX_ICON_BYTES) {
      toast(`파일이 너무 큽니다 · 최대 ${MAX_ICON_MB}MB (선택: ${(file.size / 1024 / 1024).toFixed(1)}MB)`);
      iconFile.value = '';
      return;
    }
    const rawDataUrl = await readAsDataUrl(file).catch(() => null);
    if (!rawDataUrl) { toast('파일을 읽지 못했습니다'); iconFile.value = ''; return; }
    // Auto-optimize raster images (safe — our own asset). Use it only if smaller.
    let dataUrl = rawDataUrl;
    const optimized = await optimizeIconImage(file, rawDataUrl);
    if (optimized) {
      dataUrl = optimized;
      toast(`이미지 최적화 · ${fmtBytes(rawDataUrl.length)} → ${fmtBytes(optimized.length)}`);
    }
    const wasVideo = frameDoc()?.getElementById(ICON_EL_ID)?.tagName === 'VIDEO';
    edits.icon = dataUrl;
    edits.iconType = guessIconType(dataUrl);   // trust the resulting media's real type
    syncIconControls();
    if (iconReset) iconReset.hidden = false;
    // A video swap changes the element tag; rebuild the preview so drag/resize
    // rebinds to the new node. Image↔image just patches src in place.
    if (edits.iconType === 'video' || wasVideo) loadPreview();
    else patchLiveIcon(dataUrl, edits.iconType);
    updateSaveState();
  });
  iconReset?.addEventListener('click', () => {
    const wasVideo = frameDoc()?.getElementById(ICON_EL_ID)?.tagName === 'VIDEO';
    edits.icon = defaults.icon ?? '';
    edits.iconType = defaults.iconType;
    syncIconControls();
    iconReset.hidden = true;
    if (wasVideo || edits.iconType === 'video') loadPreview();
    else patchLiveIcon(edits.icon, edits.iconType);
    updateSaveState();
  });

  // Icon corner radius (0–50%).
  const iconRadius = host.querySelector('#plIconRadius');
  const iconRadiusVal = host.querySelector('#plIconRadiusVal');
  iconRadius?.addEventListener('input', () => {
    edits.iconRadius = +iconRadius.value;
    if (iconRadiusVal) iconRadiusVal.textContent = edits.iconRadius + '%';
    const thumb = host.querySelector('.ed-image-preview');
    if (thumb) thumb.style.borderRadius = edits.iconRadius + '%';
    patchLiveIconRadius();
    updateSaveState();
  });

  // Icon hook animation.
  const iconFx = host.querySelector('#plIconFx');
  iconFx?.addEventListener('change', () => {
    edits.iconFx = iconFx.value;
    patchLiveIconFx();
    updateSaveState();
  });

  // Video fullscreen — fills the whole screen; disables position/size editing.
  const iconFull = host.querySelector('#plIconFull');
  iconFull?.addEventListener('change', () => {
    edits.iconFullscreen = iconFull.checked;
    loadPreview();   // rebuild so the icon manipulation re-binds (dis/enabled)
    updateSaveState();
  });

  // Background color — always applied.
  const bgColor = host.querySelector('#plBgColor');
  const bgHex = host.querySelector('#plBgHex');
  bgColor?.addEventListener('input', () => {
    edits.bgColor = bgColor.value;
    if (bgHex) bgHex.textContent = bgColor.value.toUpperCase();
    patchLiveBg(bgColor.value);
    updateSaveState();
  });

  // Preload gauge — visibility toggle + selector (auto-detected, editable)
  const gaugeOn = host.querySelector('#plGaugeOn');
  const gaugeSel = host.querySelector('#plGaugeSel');
  gaugeOn?.addEventListener('change', () => {
    edits.gaugeOn = gaugeOn.checked;
    patchLiveGauge(edits.gaugeOn, edits.gaugeSelector);
    updateSaveState();
  });
  gaugeSel?.addEventListener('change', () => {
    edits.gaugeSelector = gaugeSel.value.trim();
    const hint = host.querySelector('#plGaugeHint');
    if (hint) hint.textContent = edits.gaugeSelector ? '미리보기에서 드래그해 위치 이동' : '선택자를 입력하면 프로그래스바를 이동할 수 있습니다';
    // Re-resolve the progress bar in the preview against the new selector.
    loadPreview();
    updateSaveState();
  });

  // Progress-bar color — always applied (colors the bar's fill + outline ring).
  const barColor = host.querySelector('#plBarColor');
  const barHex = host.querySelector('#plBarHex');
  barColor?.addEventListener('input', () => {
    edits.barColor = barColor.value;
    if (barHex) barHex.textContent = barColor.value.toUpperCase();
    patchLiveBarColor(edits.gaugeSelector, edits.barColor);
    updateSaveState();
  });

  host.querySelector('#plSave')?.addEventListener('click', commit);
  host.querySelector('#plResetBtn')?.addEventListener('click', () => {
    edits = {
      name: defaults.name ?? '', icon: defaults.icon ?? '',
      nameVisible: defaults.nameVisible, iconType: defaults.iconType,
      iconRadius: defaults.iconRadius, iconFx: defaults.iconFx,
      iconFullscreen: defaults.iconFullscreen, bgColor: defaults.bgColor,
      gaugeSelector: defaults.gaugeSelector, gaugeOn: defaults.gaugeOn,
      barColor: defaults.barColor,
      layout: cloneLayout(defaults.layout),
    };
    manipSel = null;
    renderEditor();
  });

  // Preview orientation (세로/가로) — verification only, not baked into export.
  // Flipping the frame's aspect resizes the iframe, so the template reflows to the
  // new aspect and layout issues in landscape/other ratios become visible.
  root.querySelectorAll('#plOrient .ed-seg').forEach(btn => {
    btn.addEventListener('click', () => {
      const landscape = btn.dataset.orient === 'landscape';
      if (landscape === previewLandscape) return;
      previewLandscape = landscape;
      root.querySelectorAll('#plOrient .ed-seg').forEach(b => b.classList.toggle('active', b === btn));
      root.querySelector('.pl-phone')?.classList.toggle('landscape', landscape);
      // Let the iframe reflow at the new aspect, then apply THIS orientation's own
      // layout, re-clamp into its SafeArea, and re-place the selection overlay.
      setTimeout(() => { applyLiveLayout(); (manipReclamp || manipPlace)?.(); }, 80);
    });
  });

  root.querySelector('#plLayoutReset')?.addEventListener('click', () => {
    if (edits.layout) edits.layout[orientKey()] = emptyLayout();   // reset only the current orientation
    manipSel = null;
    applyLiveLayout();
    if (manipPlace) manipPlace();
    updateSaveState();
  });
}

// Cheap estimate of the exported byte size: cached base bytes + the icon asset's
// data-URL delta (the only MB-scale variable; name/styles are negligible). Avoids
// re-running the multi-MB export on every keystroke/drag.
function estimateExportBytes() {
  if (!baseBytes) return 0;
  const oldIcon = defaults.icon ?? '', newIcon = edits.icon ?? '';
  return baseBytes + (newIcon !== oldIcon ? newIcon.length - oldIcon.length : 0);
}

function updateSaveState() {
  const btn = root.querySelector('#plSave');
  const hint = root.querySelector('#plHint');
  const sizeEl = root.querySelector('#plSize');
  const isUpload = loadedFrom && loadedFrom.type === 'upload';
  // Upload: exporting a copy is always allowed. Library: only persist real changes.
  const enabled = !saveInFlight && (isUpload || dirty());
  if (btn) btn.disabled = !enabled;
  if (sizeEl) {
    const total = estimateExportBytes();
    const addMB = Math.max(0, total - baseBytes) / 1048576;
    const mb = total / 1048576;
    sizeEl.textContent = addMB >= 0.05 ? `${mb.toFixed(1)}MB (+${addMB.toFixed(1)})` : `${mb.toFixed(1)}MB`;
    sizeEl.className = 'pl-size ' + (mb < 4 ? 'green' : mb < 5 ? 'yellow' : 'red');
    sizeEl.title = `예상 출력 · base ${(baseBytes / 1048576).toFixed(1)}MB + 추가 ${addMB.toFixed(2)}MB = ${mb.toFixed(1)}MB (4MB 미만 초록 · 5MB 미만 노랑 · 5MB 이상 빨강)`;
  }
  if (!hint) return;
  if (saveInFlight) hint.textContent = '저장 중…';
  else if (isUpload) hint.textContent = dirty() ? '편집한 HTML을 내려받습니다' : '변경 후 다운로드할 수 있습니다';
  else hint.textContent = dirty() ? '변경됨 · 저장하면 베이스 빌드에 반영됩니다' : '변경 사항 없음';
}

// ===== Live preview =====
// Show ONLY the preload screen, looping forever. The iframe runs with scripts
// disabled (sandbox has no allow-scripts), so the playable never boots to cover
// the preload screen — the initial DOM (the loading screen) is all that renders.
// A injected stylesheet forces every CSS animation (spinner / progress) to loop
// infinitely so the screen keeps "loading" indefinitely.
const LOOP_STYLE = `<style id="__pawPreviewLoop">html,body{margin:0!important;overflow:hidden!important}*,*::before,*::after{animation-iteration-count:infinite!important;animation-play-state:running!important}</style>`;

function buildPreviewDoc(source) {
  if (/<\/head>/i.test(source)) return source.replace(/<\/head>/i, LOOP_STYLE + '</head>');
  if (/<head[^>]*>/i.test(source)) return source.replace(/<head[^>]*>/i, (m) => m + LOOP_STYLE);
  if (/<body[^>]*>/i.test(source)) return source.replace(/<body[^>]*>/i, (m) => m + LOOP_STYLE);
  return LOOP_STYLE + source;
}

function loadPreview() {
  const frame = root.querySelector('#plFrame');
  if (!frame) return;
  revokeBlob();
  const blob = new Blob([buildPreviewDoc(baseSource)], { type: 'text/html' });
  blobUrl = URL.createObjectURL(blob);
  frame.addEventListener('load', () => {
    patchLiveName(edits.name);
    patchLiveNameVisibility(edits.nameVisible);
    patchLiveIcon(edits.icon, edits.iconType);
    patchLiveIconRadius();
    patchLiveIconFx();
    patchLiveIconFullscreen();
    patchLiveIconVideo();
    patchLiveBg(edits.bgColor);
    patchLiveGauge(edits.gaugeOn, edits.gaugeSelector);
    patchLiveBarColor(edits.gaugeSelector, edits.barColor);
    setupManipulation();
  }, { once: true });
  frame.src = blobUrl;
}

function frameDoc() {
  const frame = root && root.querySelector('#plFrame');
  try { return frame ? frame.contentDocument : null; } catch { return null; }
}
function patchLiveName(value) {
  if (value === undefined) return;
  const el = frameDoc()?.getElementById(NAME_EL_ID);
  if (el) el.textContent = value;
}
function patchLiveNameVisibility(visible) {
  const el = frameDoc()?.getElementById(NAME_EL_ID);
  if (el) el.style.display = visible ? '' : 'none';
}
// Update the icon in the preview. When the desired media type (image vs. video)
// differs from the element already in the iframe, swap the tag in place (keeping
// its id + class) so a clip actually plays; otherwise just replace src.
function patchLiveIcon(value, type) {
  if (value === undefined) return;
  const doc = frameDoc();
  const cur = doc?.getElementById(ICON_EL_ID);
  if (!cur) return;
  const wantVideo = type === 'video';
  if (wantVideo === (cur.tagName === 'VIDEO')) {
    cur.setAttribute('src', value);
    if (wantVideo) { try { cur.play?.(); } catch { /* autoplay is best-effort */ } }
    return;
  }
  const el = doc.createElement(wantVideo ? 'video' : 'img');
  el.id = ICON_EL_ID;
  if (cur.className) el.className = cur.className;
  if (wantVideo) { el.autoplay = el.loop = el.muted = true; el.setAttribute('playsinline', ''); }
  el.setAttribute('src', value);
  cur.replaceWith(el);
  if (wantVideo) { try { el.play?.(); } catch { /* autoplay is best-effort */ } }
}
function patchLiveBg(color) {
  const doc = frameDoc();
  if (!doc || !doc.head) return;
  let st = doc.getElementById('__pawBgLive');
  if (!color) { if (st) st.remove(); return; }
  if (!st) { st = doc.createElement('style'); st.id = '__pawBgLive'; doc.head.appendChild(st); }
  st.textContent = bgCss(color);
}
function patchLiveGauge(on, selector) {
  const doc = frameDoc();
  if (!doc || !doc.head) return;
  let st = doc.getElementById('__pawHideGaugeLive');
  if (on || !selector) { if (st) st.remove(); return; }
  if (!st) { st = doc.createElement('style'); st.id = '__pawHideGaugeLive'; doc.head.appendChild(st); }
  st.textContent = `${selector}{display:none!important}`;
}
// Apply the icon corner radius in the preview (only when it differs from the
// template default, so an untouched icon shows the template's own rounding).
function patchLiveIconRadius() {
  const doc = frameDoc();
  if (!doc || !doc.head) return;
  let st = doc.getElementById('__pawIconRadiusLive');
  // Radius only for images (hidden for GIF/video), and never under fullscreen.
  if (edits.iconType !== 'image' || edits.iconRadius === defaults.iconRadius) { if (st) st.remove(); return; }
  if (!st) { st = doc.createElement('style'); st.id = '__pawIconRadiusLive'; doc.head.appendChild(st); }
  st.textContent = iconRadiusCss(edits.iconRadius);
}
function patchLiveIconFx() {
  const doc = frameDoc();
  if (!doc || !doc.head) return;
  let st = doc.getElementById('__pawIconFxLive');
  const css = edits.iconType === 'image' ? iconFxCss(edits.iconFx) : '';   // effects are image-only
  if (!css) { if (st) st.remove(); return; }
  if (!st) { st = doc.createElement('style'); st.id = '__pawIconFxLive'; doc.head.appendChild(st); }
  st.textContent = css;
}
function patchLiveIconFullscreen() {
  const doc = frameDoc();
  if (!doc || !doc.head) return;
  let st = doc.getElementById('__pawIconFullLive');
  if (!iconFullscreenActive()) { if (st) st.remove(); return; }
  if (!st) { st = doc.createElement('style'); st.id = '__pawIconFullLive'; doc.head.appendChild(st); }
  st.textContent = iconFullscreenCss();
}
function patchLiveIconVideo() {
  const doc = frameDoc();
  if (!doc || !doc.head) return;
  let st = doc.getElementById('__pawIconVideoLive');
  if (edits.iconType !== 'video') { if (st) st.remove(); return; }
  if (!st) { st = doc.createElement('style'); st.id = '__pawIconVideoLive'; doc.head.appendChild(st); }
  st.textContent = iconVideoCss();
}
function patchLiveBarColor(selector, color) {
  const doc = frameDoc();
  if (!doc || !doc.head) return;
  let st = doc.getElementById('__pawBarColorLive');
  if (!selector) { if (st) st.remove(); return; }   // no bar detected → nothing to color
  if (!st) { st = doc.createElement('style'); st.id = '__pawBarColorLive'; doc.head.appendChild(st); }
  st.textContent = barColorCss(selector, color);
}

// ===== Direct manipulation (drag to move · corner handle to resize) =====
// Positions are viewport percentages (center x/y). Icon/gauge store independent
// width + height (the corner handle adjusts each axis separately); name stores a
// font-size. All sizes are in `vmax` (the larger viewport dimension, matching the
// template) so both axes share one reference — rotating the preview scales the
// element uniformly and keeps the aspect the user set (vw/vh would swap and
// distort it). Movement and resizing are clamped to a SafeArea (an inset kept
// clear on every edge). Applied via a live <style> and baked into export the same way.
let manipPlace = null;    // repositions the selection overlay; set once per preview load
let manipReclamp = null;  // re-clamps overridden positions into the current orientation's SafeArea

const SAFE_INSET = 4;   // % of the viewport kept clear on every edge (SafeArea margin)
const MIN_EXT = 2;      // % minimum element extent per axis when resizing

// Clamp a center coordinate (%) so an element of half-extent `half` (%) stays
// within the SafeArea on that axis; centers oversized elements.
function clampCenter(c, half) {
  const lo = SAFE_INSET + half, hi = 100 - SAFE_INSET - half;
  return lo > hi ? 50 : Math.min(Math.max(c, lo), hi);
}
// Max extent (%) that keeps an element centered at `c` inside the SafeArea.
function maxExtent(c) {
  return Math.max(MIN_EXT, 2 * Math.min(c - SAFE_INSET, 100 - SAFE_INSET - c));
}

// kind: 'name' → font-size + nowrap (text scales uniformly) · 'icon'/'gauge' →
// independent width + height so the corner handle adjusts each axis separately.
// ALL sizes are in vmax (the larger viewport dimension) — both axes share one
// reference, so rotating the preview scales the element uniformly and keeps the
// aspect the user set (vw/vh would swap on rotation and distort it).
function layoutRule(selector, o, kind) {
  // Center via the individual `translate` property (not `transform`) so an icon
  // hook animation (which uses `transform`) composes with it instead of clobbering it.
  const base = `${selector}{position:fixed!important;left:${o.x}%!important;top:${o.y}%!important;`
             + `translate:-50% -50%!important;margin:0!important;z-index:2147483000!important;`;
  if (kind === 'name')
    return base + `font-size:${o.size}vmax!important;white-space:nowrap!important}`;
  return base + `width:${o.w}vmax!important;height:${o.h}vmax!important}`;
}
function layoutCssFor(L) {
  L = L || {};
  const parts = [];
  if (L.name) parts.push(layoutRule('[id="parameter/preloader/name"]', L.name, 'name'));
  if (L.icon && !iconFullscreenActive()) parts.push(layoutRule('[id="asset/preloader/icon"]', L.icon, 'icon'));
  if (L.gauge && edits.gaugeSelector) parts.push(layoutRule(edits.gaugeSelector, L.gauge, 'gauge'));
  return parts.join('');
}
// Live preview: only the current orientation's layout (the iframe is in that orientation).
function layoutCss() { return layoutCssFor(curLayout()); }
// Export: bake BOTH orientations, each guarded by its @media (orientation) query.
function exportLayoutCss() {
  const p = layoutCssFor(edits.layout && edits.layout.portrait);
  const l = layoutCssFor(edits.layout && edits.layout.landscape);
  return (p ? `@media (orientation:portrait){${p}}` : '')
       + (l ? `@media (orientation:landscape){${l}}` : '');
}
function applyLiveLayout() {
  const doc = frameDoc();
  if (!doc || !doc.head) return;
  let st = doc.getElementById('__pawLayoutLive');
  const css = layoutCss();
  if (!css) { if (st) st.remove(); if (manipPlace) manipPlace(); return; }
  if (!st) { st = doc.createElement('style'); st.id = '__pawLayoutLive'; doc.head.appendChild(st); }
  st.textContent = css;
}

const MANIP_STYLE = `#__pawSel{position:fixed;box-sizing:border-box;border:1.5px dashed #ff5db1;pointer-events:none;z-index:2147483600;display:none}#__pawSel .__pawHandle{position:absolute;right:-8px;bottom:-8px;width:15px;height:15px;border-radius:50%;background:#ff5db1;box-shadow:0 0 0 2px #fff;pointer-events:auto;cursor:nwse-resize}#__pawSel .__pawHandle:hover{transform:scale(1.15)}`;

function setupManipulation() {
  const frame = root && root.querySelector('#plFrame');
  const doc = frameDoc();
  const win = frame && frame.contentWindow;
  if (!doc || !win || !doc.body) return;

  applyLiveLayout();

  // Selection overlay (outline + corner handle) lives inside the iframe.
  if (!doc.getElementById('__pawManipStyle')) {
    const s = doc.createElement('style'); s.id = '__pawManipStyle'; s.textContent = MANIP_STYLE; doc.head.appendChild(s);
  }
  let ov = doc.getElementById('__pawSel');
  if (!ov) { ov = doc.createElement('div'); ov.id = '__pawSel'; ov.innerHTML = '<div class="__pawHandle"></div>'; doc.body.appendChild(ov); }
  const handle = ov.firstChild;

  let gaugeEl = null;
  if (edits.gaugeSelector) { try { gaugeEl = doc.querySelector(edits.gaugeSelector); } catch { gaugeEl = null; } }
  const els = { name: doc.getElementById(NAME_EL_ID), icon: doc.getElementById(ICON_EL_ID), gauge: gaugeEl };
  if (iconFullscreenActive()) els.icon = null;   // fullscreen video: no drag/resize
  const vw = () => win.innerWidth || 1;
  const vh = () => win.innerHeight || 1;

  function place() {
    const el = manipSel && els[manipSel];
    const hidden = (manipSel === 'name' && !edits.nameVisible) || (manipSel === 'gauge' && !edits.gaugeOn);
    if (!el || hidden) { ov.style.display = 'none'; return; }
    const r = el.getBoundingClientRect();
    ov.style.display = 'block';
    ov.style.left = r.left + 'px'; ov.style.top = r.top + 'px';
    ov.style.width = r.width + 'px'; ov.style.height = r.height + 'px';
  }
  manipPlace = place;

  const vmax = () => Math.max(vw(), vh());

  // Re-fit every overridden element into the CURRENT orientation's SafeArea after
  // rotating the preview. Sizes are vmax (the larger dimension), so a width that
  // fit in landscape (where width IS the large dim) overflows the short width in
  // portrait — shrink it first, then nudge the center back inside. Only ever
  // shrinks, so it converges to a size/position that's safe in BOTH orientations.
  function reclampAll() {
    let changed = false;
    const shrink = (over) => over > 1.005;
    const L = curLayout() || {};
    for (const kind of ['name', 'icon', 'gauge']) {
      const o = L[kind], el = els[kind];
      if (!o || !el) continue;
      let r = el.getBoundingClientRect();
      let sized = false;
      const overW = () => (r.width / vw() * 100) / maxExtent(o.x);
      const overH = () => (r.height / vh() * 100) / maxExtent(o.y);
      // 1) shrink to fit the SafeArea — uniformly by the worse axis so the aspect
      //    the user set is preserved (only the overall scale drops).
      const over = Math.max(overW(), overH());
      if (shrink(over)) {
        if (kind === 'name') o.size = +(o.size / over).toFixed(2);
        else { o.w = +(o.w / over).toFixed(2); o.h = +(o.h / over).toFixed(2); }
        sized = true;
      }
      if (sized) { changed = true; applyLiveLayout(); r = el.getBoundingClientRect(); }
      // 2) clamp the center using the (possibly shrunk) extent
      const nx = +clampCenter(o.x, r.width / vw() * 100 / 2).toFixed(2);
      const ny = +clampCenter(o.y, r.height / vh() * 100 / 2).toFixed(2);
      if (nx !== o.x || ny !== o.y) { o.x = nx; o.y = ny; changed = true; }
    }
    if (changed) { applyLiveLayout(); updateSaveState(); }
    if (manipPlace) manipPlace();
  }
  manipReclamp = reclampAll;

  // Freeze an element's current template position/size into an override object so
  // the very first drag doesn't jump. name → {x, y, size} (font-size, vmax); icon/
  // gauge → {x, y, w, h} (independent width + height, both vmax).
  function ensureOverride(kind) {
    if (curLayout()[kind]) return;
    const el = els[kind]; if (!el) return;
    const r = el.getBoundingClientRect();
    const o = {
      x: +((r.left + r.width / 2) / vw() * 100).toFixed(2),
      y: +((r.top + r.height / 2) / vh() * 100).toFixed(2),
    };
    if (kind === 'name') o.size = +(parseFloat(win.getComputedStyle(el).fontSize) / vmax() * 100).toFixed(2);
    else { o.w = +(r.width / vmax() * 100).toFixed(2); o.h = +(r.height / vmax() * 100).toFixed(2); }
    curLayout()[kind] = o;
  }

  let drag = null;
  function start(kind, e, mode) {
    if (!els[kind]) return;
    e.preventDefault(); e.stopPropagation();
    manipSel = kind;
    ensureOverride(kind);
    applyLiveLayout();
    const o = curLayout()[kind];
    drag = {
      kind, mode, sx: e.clientX, sy: e.clientY,
      ox: o.x, oy: o.y, ow: o.w, oh: o.h,
      startFsPx: kind === 'name' ? parseFloat(win.getComputedStyle(els[kind]).fontSize) : 0,
    };
    try { (mode === 'resize' ? handle : els[kind]).setPointerCapture(e.pointerId); } catch { /* */ }
    place();
    updateManipHint();
  }
  function move(e) {
    if (!drag) return;
    e.preventDefault();
    const o = curLayout()[drag.kind];
    if (drag.mode === 'move') {
      // Clamp the center by the element's live half-extent so its box stays in the SafeArea.
      const r = els[drag.kind].getBoundingClientRect();
      o.x = +clampCenter(drag.ox + (e.clientX - drag.sx) / vw() * 100, r.width / vw() * 100 / 2).toFixed(2);
      o.y = +clampCenter(drag.oy + (e.clientY - drag.sy) / vh() * 100, r.height / vh() * 100 / 2).toFixed(2);
    } else if (drag.kind === 'name') {
      // Text scales uniformly via font-size (vertical drag).
      const npx = Math.max(6, drag.startFsPx + (e.clientY - drag.sy) * 0.6);
      o.size = +(npx / vmax() * 100).toFixed(2);
      applyLiveLayout();
      const r = els.name.getBoundingClientRect();
      const over = Math.max((r.width / vw() * 100) / maxExtent(o.x), (r.height / vh() * 100) / maxExtent(o.y), 1);
      if (over > 1) o.size = +(o.size / over).toFixed(2);
    } else {
      // icon / gauge — independent width (horizontal drag) + height (vertical drag),
      // both in vmax. Clamp each axis to the SafeArea after applying.
      o.w = +Math.max(MIN_EXT, drag.ow + (e.clientX - drag.sx) / vmax() * 100).toFixed(2);
      o.h = +Math.max(MIN_EXT, drag.oh + (e.clientY - drag.sy) / vmax() * 100).toFixed(2);
      applyLiveLayout();
      const r = els[drag.kind].getBoundingClientRect();
      const overW = (r.width / vw() * 100) / maxExtent(o.x);
      const overH = (r.height / vh() * 100) / maxExtent(o.y);
      if (overW > 1) o.w = +(o.w / overW).toFixed(2);
      if (overH > 1) o.h = +(o.h / overH).toFixed(2);
    }
    applyLiveLayout();
    place();
    updateSaveState();
  }
  function end() { drag = null; }

  if (els.name) { els.name.style.cursor = 'move'; els.name.addEventListener('pointerdown', (e) => start('name', e, 'move')); }
  if (els.icon) { els.icon.style.cursor = 'move'; els.icon.draggable = false; els.icon.addEventListener('pointerdown', (e) => start('icon', e, 'move')); }
  if (els.gauge) { els.gauge.style.cursor = 'move'; els.gauge.addEventListener('pointerdown', (e) => start('gauge', e, 'move')); }
  handle.addEventListener('pointerdown', (e) => start(manipSel || 'name', e, 'resize'));
  doc.addEventListener('pointermove', move);
  doc.addEventListener('pointerup', end);
  doc.addEventListener('pointercancel', end);
  // Click empty background → deselect
  doc.addEventListener('pointerdown', (e) => {
    if (e.target === doc.body || e.target === doc.documentElement) { manipSel = null; place(); updateManipHint(); }
  });

  place();
  updateManipHint();
}

function updateManipHint() {
  const hint = root && root.querySelector('#plManipHint');
  if (!hint) return;
  const label = manipSel === 'name' ? '게임 이름' : manipSel === 'icon' ? '아이콘' : manipSel === 'gauge' ? '프로그래스바' : null;
  hint.textContent = label
    ? `선택: ${label} · 드래그로 이동 · 핸들로 크기`
    : '요소를 드래그해 위치 이동 · 모서리 핸들로 크기 조정';
}

// ===== Export (bake all edits into real HTML) =====
// Background is applied as a high-specificity <style> (targeting body + the
// ancestor that contains both preload elements) rather than by editing the
// template's own CSS — no DOM round-trip, so the multi-MB build stays intact.
function bgCss(color) {
  return `html,body{background:${color}!important}`
       + `*:has([id="asset/preloader/icon"]):has([id="parameter/preloader/name"]){background:${color}!important}`;
}

// Progress-bar color: paint the fill (the bar's descendants — templates nest the
// moving fill inside the detected outer/track element) and match its outline ring
// (the track's ::before border), leaving the track itself transparent so the fill
// still reads as progress.
function barColorCss(selector, color) {
  return `${selector} *{background:${color}!important}`
       + `${selector}::before{border-color:${color}!important}`;
}
function iconRadiusCss(radius) {
  return `[id="asset/preloader/icon"]{border-radius:${radius}%!important}`;
}

// Replace (or remove, when css is null) a <style id> block in the head.
function injectStyle(source, id, css) {
  const out = source.replace(new RegExp(`<style id="${id}">[\\s\\S]*?</style>`, 'i'), '');
  if (!css) return out;
  const tag = `<style id="${id}">${css}</style>`;
  if (/<\/head>/i.test(out)) return out.replace(/<\/head>/i, tag + '</head>');
  if (/<head[^>]*>/i.test(out)) return out.replace(/<head[^>]*>/i, (m) => m + tag);
  return tag + out;
}

// Swap the preload icon element to the desired media kind — <img> for image/gif,
// <video> (autoplay/loop/muted) for a short clip — preserving its other attributes
// and replacing src. Matches whichever tag (img or video) currently carries the id,
// so it round-trips when a saved base was already video-ified.
function setIconMedia(source, src, type) {
  const rx = /<(?:img|video)\b([^>]*?)\sid="asset\/preloader\/icon"([^>]*?)>(?:\s*<\/video>)?/i;
  return source.replace(rx, (m, pre, post) => {
    const attrs = (pre + post)
      .replace(/\ssrc="[^"]*"/i, '')
      .replace(/\s(?:autoplay|loop|muted|playsinline)(?:="[^"]*")?/gi, '')
      .replace(/\s+/g, ' ').trim();
    const rest = attrs ? ' ' + attrs : '';
    return type === 'video'
      ? `<video id="asset/preloader/icon"${rest} src="${src}" autoplay loop muted playsinline></video>`
      : `<img id="asset/preloader/icon"${rest} src="${src}">`;
  });
}

// Produce the final, shippable HTML with every edit baked in.
function exportSource() {
  const namePatch = (edits.name !== undefined && edits.name !== (defaults.name ?? '')) ? { name: edits.name } : {};
  let out = patchPreload(baseSource, namePatch);                            // name text
  // Icon: swap media element if the src or its kind (image/gif/video) changed.
  if (edits.icon && (edits.icon !== (defaults.icon ?? '') || edits.iconType !== defaults.iconType))
    out = setIconMedia(out, edits.icon, edits.iconType);
  out = injectStyle(out, '__pawIconRadius',
    (edits.iconType === 'image' && edits.iconRadius !== defaults.iconRadius) ? iconRadiusCss(edits.iconRadius) : null);
  out = injectStyle(out, '__pawIconFx', edits.iconType === 'image' ? (iconFxCss(edits.iconFx) || null) : null);
  out = injectStyle(out, '__pawIconFull', iconFullscreenActive() ? iconFullscreenCss() : null);
  out = injectStyle(out, '__pawIconVideo', edits.iconType === 'video' ? iconVideoCss() : null);
  out = injectStyle(out, '__pawHideName',
    edits.nameVisible ? null : `[id="parameter/preloader/name"]{display:none!important}`);
  out = injectStyle(out, '__pawBg', bgCss(edits.bgColor));                 // always-on
  out = injectStyle(out, '__pawHideGauge',
    (edits.gaugeOn || !edits.gaugeSelector) ? null : `${edits.gaugeSelector}{display:none!important}`);
  out = injectStyle(out, '__pawBarColor',
    edits.gaugeSelector ? barColorCss(edits.gaugeSelector, edits.barColor) : null);   // always-on (if a bar exists)
  out = injectStyle(out, '__pawLayout', exportLayoutCss() || null);        // per-orientation position/size
  return out;
}

async function commit() {
  if (saveInFlight) return;
  if (loadedFrom.type === 'upload') { downloadEdited(); return; }
  await saveToBase();
}

function downloadEdited() {
  const blob = new Blob([exportSource()], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = loadedFrom.name || 'preload.html';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  const mb = blob.size / 1024 / 1024;
  toast(mb > OUTPUT_WARN_MB
    ? `⚠ 다운로드됨 · ${mb.toFixed(1)}MB (네트워크 ${OUTPUT_WARN_MB}MB 제한 초과)`
    : `다운로드 · ${a.download} (${mb.toFixed(1)}MB)`);
}

async function saveToBase() {
  if (!dirty()) return;
  saveInFlight = true;
  updateSaveState();
  try {
    const patched = exportSource();
    await replaceFileBase({ id: loadedFrom.fileId, source: patched });
    baseSource = patched;
    baseBytes = new Blob([patched]).size;
    // Re-baseline so the next edit diffs against the just-saved state. Must mirror
    // every field dirty() checks, or the just-saved state reads as still-dirty.
    defaults = {
      name: edits.name, icon: edits.icon,
      nameVisible: edits.nameVisible, iconType: edits.iconType,
      iconRadius: edits.iconRadius, iconFx: edits.iconFx,
      iconFullscreen: edits.iconFullscreen, bgColor: edits.bgColor,
      gaugeSelector: edits.gaugeSelector, gaugeOn: edits.gaugeOn,
      barColor: edits.barColor,
      layout: cloneLayout(edits.layout),
    };
    toast(`프리로드 저장됨 · ${loadedFrom.fileName}`);
  } catch (e) {
    console.warn('[preload] save failed:', e);
    toast('저장에 실패했습니다');
  } finally {
    saveInFlight = false;
    renderEditor();
  }
}

document.querySelector('#homeTabs a[data-home-tab="preload"]')?.addEventListener('click', render);
